import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const AuthCtx = createContext(null);

function decodeExp(token) {
  try {
    const [, payload] = token.split(".");
    const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return json.exp ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=none, obj=user
  const [ready, setReady] = useState(false);
  const timers = useRef({ warn: null, expire: null });

  const clearTimers = () => {
    if (timers.current.warn) clearTimeout(timers.current.warn);
    if (timers.current.expire) clearTimeout(timers.current.expire);
    timers.current = { warn: null, expire: null };
  };

  const scheduleExpiry = (token) => {
    clearTimers();
    if (!token) return;
    const expMs = decodeExp(token);
    if (!expMs) return;
    const now = Date.now();
    const untilExpire = expMs - now;
    const untilWarn = untilExpire - 5 * 60 * 1000; // 5 min before
    if (untilWarn > 0) {
      timers.current.warn = setTimeout(() => {
        toast.warning("Your session ends in 5 minutes — please save your work.", {
          duration: 10000,
        });
      }, untilWarn);
    }
    if (untilExpire > 0) {
      timers.current.expire = setTimeout(() => {
        toast.error("Session expired. Please log in again.");
        localStorage.removeItem("gm_token");
        setUser(false);
      }, untilExpire);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        const existing = localStorage.getItem("gm_token");
        if (existing) scheduleExpiry(existing);
      } catch {
        setUser(false);
      } finally {
        setReady(true);
      }
    })();
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) {
      localStorage.setItem("gm_token", data.token);
      scheduleExpiry(data.token);
    }
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("gm_token");
    clearTimers();
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, ready, login, logout, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
